import { Button, Checkbox, Group, Modal, SegmentedControl, Select, Stack, Switch, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useEffect, useState, type FC } from 'react';

import { DAYS } from './schedule.utils';
import { createScheduledAlert, type CreateScheduledAlertInput, deleteUserAlert, type ScheduledAlertFrequency, updateScheduledAlert, type UserAlertRow } from '@api/userAlerts';

/**
 * "Schedule this" for any artifact: pick how often and when, and the alert is created with
 * the context the caller hands in. The caller owns what is scheduled (the plan's inputs and
 * its deep link); this dialog owns the timing and auto-save. Given `existing`, it edits or
 * removes that schedule instead.
 */
export interface ScheduleAlertDialogProps {
    opened: boolean;
    onClose: () => void;
    /** Dialog title, e.g. "Schedule: Production to start today". */
    title: string;
    /** One line under the title saying what will run and where the card opens. */
    description?: string;
    /** The alert to create, minus the schedule. */
    alert: Omit<CreateScheduledAlertInput, 'frequency' | 'triggerTime' | 'triggerDayOfWeek'>;
    /** Called with the created alert id. */
    onScheduled?: (alertId: string) => void;
    /**
     * Offer "save each run on its own": the switch writes `autoSave: true` into the alert's
     * context, and the type's source saves what it computed. Absent = the run only reports.
     */
    autoSave?: { label: string; description: string };
    /**
     * The schedule already set for this artifact: the dialog edits it in place (timing, auto-save,
     * optionally this page's inputs) or removes it, instead of creating a second one.
     */
    existing?: UserAlertRow & { parsedContext: Record<string, unknown> };
    /** Called after an edit or a removal, so the caller re-reads what is scheduled. */
    onChanged?: () => void;
}


const FREQUENCIES: Array<{ value: ScheduledAlertFrequency; label: string }> = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'EVERY_12H', label: 'Every 12h' },
    { value: 'EVERY_6H', label: 'Every 6h' },
];


const ScheduleAlertDialog: FC<ScheduleAlertDialogProps> = ({ opened, onClose, title, description, alert, onScheduled, autoSave, existing, onChanged }) => {
    const [frequency, setFrequency] = useState<ScheduledAlertFrequency>('DAILY');
    const [triggerTime, setTriggerTime] = useState('07:00');
    const [dayOfWeek, setDayOfWeek] = useState('1');
    const [saving, setSaving] = useState(false);
    const [autoSaveOn, setAutoSaveOn] = useState(false);
    const [usePage, setUsePage] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(false);

    // Opening on an existing schedule starts from what it has.
    useEffect(() => {
        if (!opened) return;
        setConfirmRemove(false);
        setUsePage(false);
        if (existing) {
            setFrequency((existing.frequency as ScheduledAlertFrequency | null) ?? 'DAILY');
            setTriggerTime(existing.triggerTime?.slice(0, 5) ?? '07:00');
            setDayOfWeek(String(existing.triggerDayOfWeek ?? 1));
            setAutoSaveOn(existing.parsedContext.autoSave === true || existing.parsedContext.autoSave === 'true');
        } else {
            setFrequency('DAILY');
            setTriggerTime('07:00');
            setDayOfWeek('1');
            setAutoSaveOn(false);
        }
    }, [opened, existing]);

    const failed = (heading: string, e: unknown): void => {
        const msg = (e as { error?: { message?: string | string[] }; message?: string })?.error?.message ?? (e as Error)?.message;
        notifications.show({ color: 'red', title: heading, message: Array.isArray(msg) ? msg.join('; ') : msg || 'Unknown error' });
    };
    const when = (): string => (frequency === 'DAILY' ? 'daily' : frequency === 'WEEKLY' ? `every ${DAYS[Number(dayOfWeek)]?.label}` : frequency === 'EVERY_12H' ? 'every 12 hours' : 'every 6 hours');

    const submit = async () => {
        if (!/^\d{2}:\d{2}$/.test(triggerTime)) {
            notifications.show({ color: 'red', message: 'Pick a time as HH:mm.' });
            return;
        }
        setSaving(true);
        // The switch is always written, on or off, so the schedule carries the setting explicitly.
        const withAutoSave = (ctx: Record<string, unknown>): Record<string, unknown> => (autoSave ? { ...ctx, autoSave: autoSaveOn } : ctx);
        try {
            if (existing) {
                await updateScheduledAlert(existing.alertId, {
                    alertType: existing.alertType,
                    context: withAutoSave(usePage ? alert.context : existing.parsedContext),
                    frequency,
                    triggerTime,
                    triggerDayOfWeek: frequency === 'WEEKLY' ? Number(dayOfWeek) : null,
                });
                notifications.show({ color: 'green', title: 'Schedule updated', message: `${existing.alertLabel ?? alert.alertLabel} runs ${when()} at ${triggerTime}${autoSave ? (autoSaveOn ? ', saving the plan each run' : ', without saving') : ''}.` });
                onChanged?.();
                onClose();
                return;
            }
            const created = await createScheduledAlert({
                ...alert,
                context: withAutoSave(alert.context),
                frequency,
                triggerTime,
                triggerDayOfWeek: frequency === 'WEEKLY' ? Number(dayOfWeek) : undefined,
            });
            notifications.show({
                color: 'green',
                title: 'Scheduled',
                message: `${alert.alertLabel} runs ${when()} at ${triggerTime}. It shows on Home after its first run.`,
            });
            onScheduled?.(created.alertId);
            onChanged?.();
            onClose();
        } catch (e) {
            failed(existing ? 'Could not update the schedule' : 'Could not schedule', e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!existing) return;
        if (!confirmRemove) {
            setConfirmRemove(true);
            return;
        }
        setSaving(true);
        try {
            await deleteUserAlert(existing.alertId);
            notifications.show({ color: 'gray', title: 'Schedule removed', message: `${existing.alertLabel ?? alert.alertLabel} no longer runs. Saved plans stay.` });
            onChanged?.();
            onClose();
        } catch (e) {
            failed('Could not remove the schedule', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} centered size="sm">
            <Stack gap="md">
                {description && (
                    <Text size="sm" c="dimmed">
                        {description}
                    </Text>
                )}
                <SegmentedControl
                    size="xs"
                    value={frequency}
                    onChange={(v) => setFrequency(v as ScheduledAlertFrequency)}
                    data={FREQUENCIES}
                />
                {frequency === 'WEEKLY' && (
                    <Select label="Day" size="xs" data={DAYS} value={dayOfWeek} onChange={(v) => setDayOfWeek(v ?? '1')} allowDeselect={false} />
                )}
                <TimeInput
                    label={frequency === 'EVERY_6H' || frequency === 'EVERY_12H' ? 'First run at' : 'Run at'}
                    size="xs"
                    value={triggerTime}
                    onChange={(e) => setTriggerTime(e.currentTarget.value)}
                />
                {autoSave && (
                    <Switch
                        size="xs"
                        checked={autoSaveOn}
                        onChange={(e) => setAutoSaveOn(e.currentTarget.checked)}
                        label={autoSave.label}
                        description={autoSave.description}
                    />
                )}
                {existing && (
                    <Checkbox
                        size="xs"
                        checked={usePage}
                        onChange={(e) => setUsePage(e.currentTarget.checked)}
                        label="Use this page's settings"
                        description="Off: the schedule keeps the inputs it was set up with. On: it takes what this page has now."
                    />
                )}
                <Group justify="space-between" gap="xs">
                    {existing ? (
                        <Button size="xs" variant="subtle" color="red" onClick={() => void remove()} disabled={saving}>
                            {confirmRemove ? 'Click again to remove' : 'Remove schedule'}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Group gap="xs">
                        <Button size="xs" variant="default" onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button size="xs" onClick={() => void submit()} loading={saving}>
                            {existing ? 'Save schedule' : 'Schedule'}
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ScheduleAlertDialog;
